import { Combobox } from "@headlessui/react";
import { useState, Fragment } from "react";
import Dialog from "@mui/material/Dialog";
import { hasImage, playerList } from "../lib/utils/playerList";
import { MagnifyingGlassIcon } from "./icons/MagnifyingGlassIcon";
import { MAX_GUESSES } from "../lib/utils/constants";
import { gridLevelDateKey, gridLevelNumber } from "../lib/utils/gridLevels";
import { trackLevelStart } from "../lib/utils/analytics";
import { IconCheckLg } from "./icons/IconCheckLg";
import { UndoButton } from "./buttons/UndoButton";
import PickDialog from "./modals/PickDialog";
import { rarityScheme } from "../lib/utils/rarityScheme";
import useGameStore from "../lib/store/GameStore";
import usePersistentStore from "../lib/store/PersistentStore";

function getKeyOfMaxValue(obj) {
  let maxKey = null;
  let maxValue = Number.NEGATIVE_INFINITY;

  for (const key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] > maxValue) {
      maxKey = key;
      maxValue = obj[key];
    }
  }

  return maxKey;
}

export default function CellButton({ row, col, selectedTab }) {
  const [
    grid,
    setIsResultOpen,
    setIsCaptureOpen,
    analyticsGameName,
    globalStats,
    undoModeActive,
    user,
    setIsRegisterOpen,
  ] = useGameStore((state) => [
    state.grid,
    state.setIsResultOpen,
    state.setIsCaptureOpen,
    state.analyticsGameName,
    state.globalStats,
    state.undoModeActive,
    state.user,
    state.setIsRegisterOpen,
  ]);

  const [
    persistGuess,
    undoGuess,
    guessesLeft,
    incorrectGuesses,
    correctGuesses,
    undoUsed,
    seenRarity,
    setSeenRarity,
    cardMode,
  ] = usePersistentStore((state) => [
    state.persistGuess,
    state.undoGuess,
    state.guessesLeft,
    state.incorrectGuesses,
    state.correctGuesses,
    state.undoUsed,
    state.seenRarity,
    state.setSeenRarity,
    state.cardMode,
  ]);

  const storedCorrectGuessId = correctGuesses[row - 1][col - 1];
  const storedCorrectPlayer = playerList.find(
    (player) => player.id === storedCorrectGuessId
  );
  const otherCorrectGuesses = correctGuesses.flat();
  const correctAnswers = Object.keys(
    grid.correctAnswersPlayerMap[row - 1][col - 1]
  ).map((playerId) => parseInt(playerId));
  const cellGuesses = incorrectGuesses[row - 1][col - 1];

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(false);
  const [query, setQuery] = useState("");

  const correctAnswersPlayerMap =
    globalStats?.correctAnswersPlayerMap[row - 1][col - 1];
  const mostPopularId = getKeyOfMaxValue(correctAnswersPlayerMap);
  const mostPopularPlayer = playerList.find(
    (player) => player.id == mostPopularId
  );

  const displayPlayer =
    selectedTab == "popular" ? mostPopularPlayer : storedCorrectPlayer;

  const selectedPct =
    displayPlayer && correctAnswersPlayerMap
      ? (correctAnswersPlayerMap[displayPlayer.id] / globalStats?.completed) *
        100
      : 0.1;

  const filteredPeople =
    query.length < 1
      ? []
      : playerList
          .filter((player) => {
            return player.name.toLowerCase().includes(query.toLowerCase());
          })
          .slice(0, 20);

  function closeModal() {
    setIsOpen(false);
    setSelectedPerson(false);
    setQuery("");
  }

  function openModal() {
    if (grid.levelNumber !== gridLevelNumber && !user) {
      // Premum feature check
      setIsRegisterOpen(true);
    } else if (
      guessesLeft > 0 &&
      !storedCorrectPlayer &&
      selectedTab != "popular"
    ) {
      setIsOpen(true);
    }
  }

  function undoHandler(player) {
    if (selectedTab == "my") {
      undoGuess(player, row, col);
    }
  }

  function selectPlayer(player) {
    if (guessesLeft === MAX_GUESSES) {
      trackLevelStart({
        gameName: analyticsGameName,
        levelNumber: gridLevelNumber,
      });
    }
    setSelectedPerson(player);
    persistGuess(player, row, col);

    if (correctAnswers.includes(player.id)) {
      const pct = correctAnswersPlayerMap
        ? (correctAnswersPlayerMap[player.id] / globalStats?.completed) * 100
        : 0.1;
      const scheme = rarityScheme(pct);

      if (cardMode && (scheme.level > seenRarity || scheme.level == 9)) {
        closeModal();
        openPickModal();
        setSeenRarity(scheme.level);
      } else {
        setTimeout(() => {
          closeModal();
        }, 700);
      }
    }

    if (guessesLeft <= 1) {
      closeModal();

      if (gridLevelDateKey === "2024-03-04") {
        setIsCaptureOpen(true);
      } else {
        setIsResultOpen(true);
      }
    }
  }

  const [isPickOpen, setIsPickOpen] = useState(false);
  function closePickModal() {
    setIsPickOpen(false);
  }

  function openPickModal() {
    if (cardMode && selectedTab == "my") {
      setIsPickOpen(true);
    }
  }

  const scheme = rarityScheme(selectedPct);
  const bg = cardMode ? scheme.class : "bg-[#59d185]";

  return (
    <>
      {displayPlayer ? (
        <>
          <PickDialog
            isOpen={isPickOpen}
            closeModal={closePickModal}
            player={displayPlayer}
            selectedPct={selectedPct}
            row={row}
            col={col}
          />
          <button
            onClick={openPickModal}
            //onClick={() => undoHandler(displayPlayer)}
            className={`flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden bg-cover bg-center dark:border-gray-950 ${
              selectedTab == "popular" && "border-slate-500"
            }
        } ${col < 3 && "border-r"} ${row < 3 && "border-b"}  ${
              selectedTab == "popular"
                ? "bg-slate-400"
                : storedCorrectPlayer
                ? bg
                : ""
            } transition-colors duration-75 focus-visible:z-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#59d185] dark:hover:bg-gray-700 sm:h-36 sm:w-36 md:h-48 md:w-48`}
          >
            <div className="overflow group relative flex h-full w-full items-center justify-center">
              {!isNaN(selectedPct) && (
                <div className="absolute right-0 top-0 flex h-4 items-center rounded-bl-lg bg-gray-900 pl-1.5 pr-1 text-xxs text-white opacity-95 sm:h-5 sm:pr-1.5 sm:text-xs md:h-6 md:text-sm">
                  {selectedPct < 1
                    ? selectedPct.toFixed(1)
                    : selectedPct.toFixed(0)}
                  %
                </div>
              )}
              {/* <div className="absolute left-0 top-0 flex h-4 items-center rounded-br-lg bg-gray-900 pb-0.5 pl-1 pr-1.5 pt-px text-xxs text-white opacity-95 sm:h-5  sm:pl-2 sm:pr-3 sm:text-xs md:h-6 md:text-sm">
              <svg
                className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 940 1000"
              >
                <path d="M532 90c113.333 0 209.667 40 289 120s119 176.667 119 290-39.667 210-119 290-175.667 120-289 120c-92 0-176-29.333-252-88l70-76c54.667 40 115.333 60 182 60 84 0 156-30 216-90s90-132 90-216c0-85.333-30-158-90-218s-132-90-216-90c-82.667 0-153.667 28.667-213 86s-90.333 127.333-93 210h142L184 694 0 488h124c2.667-110.667 43.667-204.667 123-282S421.333 90 532 90m-36 190h70v204l130 130-50 50-150-150V280" />
              </svg>
            </div> */}
              {displayPlayer.last >= 2022 ? (
                <img
                  src={`https://s.afl.com.au/staticfile/AFL%20Tenant/AFL/Players/ChampIDImages/AFL/${displayPlayer.last}014/${displayPlayer.c}.png`}
                  className={`absolute ${
                    selectedTab == "no_spoilers" ? "opacity-80 contrast-0" : ""
                  }`}
                />
              ) : displayPlayer.last >= 2019 ? (
                <img
                  src={`https://s.afl.com.au/staticfile/AFL%20Tenant/AFL/Players/ChampIDImages/AFL/${displayPlayer.last}014/${displayPlayer.c}.png`}
                  className={`absolute -top-1 scale-110 md:-top-2 ${
                    selectedTab == "no_spoilers" ? "opacity-80 contrast-0" : ""
                  }`}
                />
              ) : displayPlayer.last >= 2013 ? (
                <img
                  src={`https://s.afl.com.au/staticfile/AFL%20Tenant/AFL/Players/ChampIDImages/AFL/${displayPlayer.last}014/${displayPlayer.c}.png`}
                  className={`absolute -top-1 sm:top-0 sm:scale-105 ${
                    selectedTab == "no_spoilers" ? "opacity-80 contrast-0" : ""
                  }`}
                />
              ) : hasImage.includes(displayPlayer.id) ? (
                <img
                  src={`/img/headshots/${displayPlayer.id}-${displayPlayer.name
                    .replaceAll(" ", "-")
                    .toLowerCase()}.png`}
                  className={`absolute h-24 sm:h-36 md:h-48 ${
                    selectedTab == "no_spoilers" ? "opacity-80 contrast-0" : ""
                  }`}
                />
              ) : (
                <img
                  src="/img/silhouette.png"
                  className={`absolute -top-1 mb-5 sm:mb-1 md:-top-2 md:mb-5 ${
                    selectedTab == "no_spoilers" ? "opacity-80 contrast-0" : ""
                  }`}
                />
              )}
              {selectedTab != "no_spoilers" && (
                <>
                  {/* <div className="absolute bottom-0 left-0 right-0">
                  {selectedTab == "my" && !undoUsed && (
                    <div
                      className={`line-clamp-1 ${
                        undoModeActive ? "flex" : "hidden"
                      } h-24 items-center justify-center bg-gradient-to-b from-gray-900/0 from-0% to-gray-900/95 py-px text-xxs text-white sm:h-36 sm:text-xs sm:group-hover:flex md:h-48 md:text-sm`}
                    >
                      <svg
                        className="mr-2 h-4 w-4"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 940 1000"
                      >
                        <path d="M532 90c113.333 0 209.667 40 289 120s119 176.667 119 290-39.667 210-119 290-175.667 120-289 120c-92 0-176-29.333-252-88l70-76c54.667 40 115.333 60 182 60 84 0 156-30 216-90s90-132 90-216c0-85.333-30-158-90-218s-132-90-216-90c-82.667 0-153.667 28.667-213 86s-90.333 127.333-93 210h142L184 694 0 488h124c2.667-110.667 43.667-204.667 123-282S421.333 90 532 90m-36 190h70v204l130 130-50 50-150-150V280" />
                      </svg>{" "}
                      Undo
                    </div>
                  )}
                </div> */}
                  <div className="absolute bottom-0 left-0 right-0">
                    {/* <div className="line-clamp-1 hidden h-40 items-center justify-center bg-gradient-to-b from-gray-900/0 from-0% to-gray-900/95 py-px text-xxs text-white group-hover:flex  sm:text-xs md:text-sm">
                    <svg
                      className="mr-2 h-4 w-4 "
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 940 1000"
                    >
                      <path d="M532 90c113.333 0 209.667 40 289 120s119 176.667 119 290-39.667 210-119 290-175.667 120-289 120c-92 0-176-29.333-252-88l70-76c54.667 40 115.333 60 182 60 84 0 156-30 216-90s90-132 90-216c0-85.333-30-158-90-218s-132-90-216-90c-82.667 0-153.667 28.667-213 86s-90.333 127.333-93 210h142L184 694 0 488h124c2.667-110.667 43.667-204.667 123-282S421.333 90 532 90m-36 190h70v204l130 130-50 50-150-150V280" />
                    </svg>{" "}
                    Undo
                  </div> */}
                    <div className="text-ellipsis whitespace-nowrap bg-gray-900 py-px text-xxs text-white opacity-95 sm:text-xs md:text-sm">
                      {displayPlayer.name.includes(" (")
                        ? displayPlayer.name.substring(
                            0,
                            displayPlayer.name.indexOf(" (")
                          )
                        : displayPlayer.name}
                    </div>
                  </div>
                </>
              )}
            </div>
          </button>
        </>
      ) : (
        <button
          onClick={openModal}
          className={`flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden dark:border-gray-950 ${
            selectedTab == "popular" && "border-slate-500"
          }
        } ${col < 3 && "border-r"} ${row < 3 && "border-b"}  ${
            selectedTab == "popular"
              ? "bg-slate-400"
              : storedCorrectPlayer
              ? bg
              : isOpen
              ? "bg-yellow-200"
              : selectedTab == "no_spoilers"
              ? ""
              : guessesLeft > 0
              ? "hover:bg-yellow-50"
              : ""
          } transition-colors duration-75 focus-visible:z-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#59d185] dark:hover:bg-gray-700 sm:h-36 sm:w-36 md:h-48 md:w-48`}
        >
          {correctAnswers.length < 0 && (
            <div className="mb-1 flex flex-col items-center text-gray-500 sm:mb-2">
              {/* <IconWarningOutline className="item h-6 w-6 sm:h-8 sm:w-8 md:h-9 md:w-9" /> */}
              <strong className="text-xs sm:text-base md:text-lg">
                {correctAnswers.length} Players
              </strong>
            </div>
          )}
        </button>
      )}

      <Dialog
        open={isOpen}
        onClose={closeModal}
        transitionDuration={300}
        componentsProps={{
          backdrop: { style: { backgroundColor: "rgba(0, 0, 0, 0.25)" } },
        }}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        className="m-4 sm:m-6 md:m-20"
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            margin: "0px",
            display: "block",
          },
        }}
        PaperProps={{
          sx: { margin: "0 auto", borderRadius: "0.75rem", maxWidth: "36rem" },
        }}
      >
        <Combobox value={selectedPerson} onChange={selectPlayer}>
          <div className="relative">
            {MagnifyingGlassIcon}
            <Combobox.Input
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
              // displayValue={(person) => person.name}
              className="h-12 w-full border-0 bg-transparent pl-11 pr-9 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-200 sm:text-sm"
              placeholder={`${correctAnswers.length} possible answers...`}
              autoComplete="off"
              autoCorrect="off"
            />
          </div>
          {filteredPeople.length > 0 && (
            <Combobox.Options
              static={true}
              className={`z-50 max-h-72 scroll-py-2 overflow-y-auto border-t border-t-gray-100 py-2 text-sm text-gray-800 dark:bg-gray-700`}
            >
              {filteredPeople.map((player) => (
                /* Use the `active` state to conditionally style the active option. */
                /* Use the `selected` state to conditionally style the selected option. */
                <Combobox.Option
                  key={player.id}
                  value={player}
                  as={Fragment}
                  disabled={
                    cellGuesses.includes(player.id) ||
                    otherCorrectGuesses.includes(player.id)
                  }
                >
                  {({ active, selected }) => (
                    <li
                      className={`flex cursor-default select-none items-center justify-between px-4 py-2 font-medium leading-snug ${
                        active ? "bg-gray-100" : ""
                      }`}
                    >
                      <div
                        className={`${
                          !storedCorrectPlayer &&
                          cellGuesses.includes(player.id) &&
                          selected &&
                          "animate-shake"
                        } ${
                          storedCorrectPlayer?.id == player.id
                            ? "text-green-600"
                            : cellGuesses.includes(player.id)
                            ? "text-red-600 dark:text-red-400"
                            : otherCorrectGuesses.includes(player.id)
                            ? "text-slate-500"
                            : "dark:text-white"
                        }`}
                      >
                        {player.name}
                        <div className="-mt-0.5 text-xs font-normal leading-snug text-gray-500 dark:text-gray-400">
                          {player.first} -{" "}
                          {player.last == 2024 ? "Present" : player.last}
                        </div>
                      </div>
                      {storedCorrectPlayer?.id == player.id && (
                        <div className="fade-in animate-correct px-2.5 py-1.5 text-sm font-medium text-green-600">
                          <IconCheckLg className="h-5 w-5" />
                        </div>
                      )}
                      {!selected &&
                        !cellGuesses.includes(player.id) &&
                        otherCorrectGuesses.includes(player.id) && (
                          <div className="px-2.5 py-1.5 text-sm font-medium text-slate-500">
                            Used
                          </div>
                        )}
                      {cellGuesses.includes(player.id) && !undoUsed && (
                        <UndoButton clickHandler={() => undoHandler(player)} />
                      )}
                    </li>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          )}
        </Combobox>
      </Dialog>
    </>
  );
}
